/**
 * Vendor detail cover — profile hero from `prototype/purchases/vendor-detail.html`.
 *
 * @see prototype/purchases/vendor-detail.html `.cover`
 */
import { Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  Check,
  CreditCard,
  FileText,
  Mail,
  MapPin,
  Pencil,
  Phone,
  User,
} from "lucide-react";
import type { VendorDetailModel, VendorDetailRow } from "../lib/detail-model.ts";
import { VendorAvatar } from "./vendor-avatar.tsx";

export interface VendorCoverProps {
  vendor: VendorDetailRow;
  model: VendorDetailModel;
  onArchive?: () => void;
  archiving?: boolean;
}

export function VendorCover({ vendor, model, onArchive, archiving }: VendorCoverProps) {
  const isArchived = vendor.archivedAt !== null;
  return (
    <section
      className="overflow-hidden rounded-md border border-line border-t-bad bg-paper shadow-sm"
      aria-label="Vendor profile"
    >
      <div className="flex flex-wrap items-start gap-4 border-b border-line p-5">
        <Link
          to="/procurement/vendors"
          search={{}}
          aria-label="Back to vendors"
          className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-line bg-paper text-muted hover:bg-bg hover:text-ink"
        >
          <ArrowLeft size={16} strokeWidth={2.2} aria-hidden />
        </Link>

        <VendorAvatar name={vendor.name} size="lg" />

        <div className="min-w-0 flex-1">
          <h1 className="truncate text-2xl font-bold tracking-tight text-ink">{vendor.name}</h1>
          <p className="mt-1 text-sm text-muted">{model.summary}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {model.pills.map((pill) => (
              <span
                key={pill.label}
                className={`inline-flex h-7 items-center rounded-full border px-3 text-xs font-semibold ${pillTone(pill.tone)}`}
              >
                {pill.label}
              </span>
            ))}
            {isArchived ? (
              <span className="inline-flex h-7 items-center rounded-full border border-line bg-raised px-3 text-xs font-semibold text-muted">
                Archived
              </span>
            ) : null}
          </div>
        </div>

        {!isArchived ? (
          <div className="flex flex-wrap items-center justify-end gap-2 sm:ml-auto">
            <Link
              to="/procurement/bills"
              className="inline-flex h-9 items-center gap-2 rounded-sm border border-line bg-paper px-3 text-sm font-semibold text-ink hover:bg-bg"
            >
              <FileText size={14} aria-hidden />
              View bill
            </Link>
            <button
              type="button"
              disabled
              className="inline-flex h-9 cursor-not-allowed items-center gap-2 rounded-sm bg-ink px-3 text-sm font-semibold text-white opacity-80"
              title="Payment approval wires up with vendor payments."
            >
              <Check size={14} aria-hidden />
              {model.awaitingBill
                ? `Approve & pay ${model.awaitingBill.amount}`
                : "No bill awaiting payment"}
            </button>
            <Link
              to="/procurement/vendors/$vendorId/edit"
              params={{ vendorId: vendor.id }}
              className="inline-flex h-9 items-center gap-2 rounded-sm border border-line bg-paper px-3 text-sm font-semibold text-ink hover:bg-bg"
            >
              <Pencil size={14} aria-hidden />
              Edit
            </Link>
            {onArchive ? (
              <button
                type="button"
                onClick={onArchive}
                disabled={archiving}
                className="inline-flex h-9 items-center rounded-sm px-3 text-sm font-semibold text-muted hover:bg-bg hover:text-ink disabled:opacity-60"
              >
                {archiving ? "Archiving…" : "Archive"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 divide-y divide-line md:grid-cols-3 md:divide-x md:divide-y-0">
        <CoverCell title="Contact" icon={<User size={14} aria-hidden />}>
          <p className="text-sm">
            <strong className="font-semibold text-ink">
              {vendor.contactName || "Contact not set"}
            </strong>
            <span className="text-muted"> · {model.contactRole}</span>
          </p>
          {vendor.phone ? (
            <p className="mt-1 text-sm font-mono">
              <a
                href={`tel:+91${vendor.phone}`}
                className="inline-flex items-center gap-1 text-sky-700 hover:underline"
              >
                <Phone size={11} aria-hidden />
                +91 {vendor.phone}
              </a>
            </p>
          ) : null}
          {vendor.email ? (
            <p className="mt-1 text-sm">
              <a
                href={`mailto:${vendor.email}`}
                className="inline-flex items-center gap-1 text-sky-700 hover:underline"
              >
                <Mail size={11} aria-hidden />
                {vendor.email}
              </a>
            </p>
          ) : null}
          <p className="mt-1 text-xs text-muted">{vendor.email || "Email not set"}</p>
        </CoverCell>

        <CoverCell title="Address" icon={<MapPin size={14} aria-hidden />}>
          <p className="text-sm font-semibold text-ink">
            {vendor.addressLine1 || "Address line 1 not set"}
          </p>
          <p className="text-sm text-ink-2">{vendor.addressLine2 || "Address line 2 not set"}</p>
          <p className="text-sm text-ink-2">
            {vendor.city || "City not set"} {vendor.pincode || "PIN not set"} ·{" "}
            {vendor.state || "State not set"}
          </p>
          <p className="mt-1 text-xs text-muted">{model.taxMode}</p>
        </CoverCell>

        <CoverCell title="GST & PAN" icon={<CreditCard size={14} aria-hidden />}>
          {vendor.gstin ? (
            <p className="text-sm">
              GSTIN <span className="font-mono">{vendor.gstin}</span>
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-ok-50 px-2 py-0.5 text-xs font-semibold text-ok">
                <Check size={10} aria-hidden />
                GSTN
              </span>
            </p>
          ) : (
            <p className="text-sm text-muted">GSTIN not provided</p>
          )}
          <p className="mt-1 text-sm">
            PAN <span className="font-mono">{model.pan ?? "Not available"}</span>
          </p>
          <p className="mt-1 text-sm text-ink-2">MSME data not connected</p>
          <p className="mt-1 text-xs text-muted">
            {vendor.paymentTerms || "Net 15"} ·{" "}
            {vendor.tdsSection ? `TDS u/s ${vendor.tdsSection} if > ₹50L` : "TDS section not set"}
          </p>
        </CoverCell>
      </div>
    </section>
  );
}

function CoverCell({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="p-5">
      <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted">
        {icon}
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

function pillTone(tone: VendorDetailModel["pills"][number]["tone"]): string {
  switch (tone) {
    case "warn":
      return "border-warn-100 bg-warn-50 text-warn";
    case "ok":
      return "border-ok-100 bg-ok-50 text-ok";
    case "sky":
      return "border-sky-100 bg-sky-50 text-sky-700";
    case "muted":
    default:
      return "border-line bg-raised text-muted";
  }
}
